// src/lib/utils/colors/color-relationships.ts

/**
 * Color relationship utilities for analyzing and generating color harmonies,
 * accessibility compliance, and perceptual color properties
 */

import type { RGB, HarmonyType, WcagLevel, SemanticPalette, PaletteAnalysis } from '~/lib/utils/colors/types'
import {
  rgbToHsl,
  hslToRgb,
  hexToRgb,
  rgbToHex,
  normalizeHue,
  rgbToLab,
  clamp
} from '~/lib/utils/colors/transforms'

/**
 * Color dominance in RGB channels
 */
export type ColorDominance = 'red' | 'green' | 'blue' | 'balanced'

/**
 * Determines which RGB channel is dominant in a color
 * @param rgb - RGB color values
 * @returns Dominant color channel or 'balanced' if similar
 */
export function getColorDominance(rgb: RGB): ColorDominance {
  const [r, g, b] = rgb
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  
  // If the difference is small, consider it balanced
  if (max - min < 30) {
    return 'balanced'
  }
  
  if (r === max) return 'red'
  if (g === max) return 'green'
  return 'blue'
}

/**
 * Calculates the saturation level of a color
 * @param rgb - RGB color values
 * @returns Saturation description
 */
export function getSaturationLevel(rgb: RGB): 'grayscale' | 'muted' | 'moderate' | 'vibrant' | 'intense' {
  const hsl = rgbToHsl(rgb)
  const saturation = hsl[1]
  
  if (saturation < 5) return 'grayscale'
  if (saturation < 25) return 'muted'
  if (saturation < 50) return 'moderate'
  if (saturation < 80) return 'vibrant'
  return 'intense'
}

/**
 * Calculates the brightness level of a color
 * @param rgb - RGB color values
 * @returns Brightness description
 */
export function getBrightnessLevel(rgb: RGB): 'very-dark' | 'dark' | 'medium' | 'light' | 'very-light' {
  const hsl = rgbToHsl(rgb)
  const lightness = hsl[2]
  
  if (lightness < 20) return 'very-dark'
  if (lightness < 40) return 'dark'
  if (lightness < 60) return 'medium'
  if (lightness < 80) return 'light'
  return 'very-light'
}


/**
 * Determines the color temperature classification of an RGB color
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {'warm' | 'cool' | 'neutral'} Color temperature classification
 * @example
 * // Classify color temperatures
 * getColorTemperature([255, 100, 50]) // 'warm' (orange-red)
 * getColorTemperature([50, 150, 255]) // 'cool' (blue)
 * getColorTemperature([128, 128, 128]) // 'neutral' (gray)
 */
export function getColorTemperature(rgb: RGB): 'warm' | 'cool' | 'neutral' {
  const hsl = rgbToHsl(rgb)
  const hue = hsl[0]
  const saturation = hsl[1]
  
  // Low saturation colors are considered neutral
  if (saturation < 15) {
    return 'neutral'
  }
  
  // Warm hues: red, orange, yellow (roughly 0-60 and 300-360)
  // Cool hues: green, blue, purple (roughly 60-300)
  if ((hue >= 0 && hue <= 60) || (hue >= 300 && hue <= 360)) {
    return 'warm'
  } else if (hue > 60 && hue < 300) {
    return 'cool'
  }
  
  return 'neutral'
}

/**
 * Calculates the relative luminance of an RGB color according to WCAG standards
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {number} Relative luminance value [0-1]
 * @example
 * // Calculate luminance for white and black
 * getLuminance([255, 255, 255]) // 1.0
 * getLuminance([0, 0, 0]) // 0.0
 */
export function getLuminance(rgb: RGB): number {
  // Convert RGB values to linear RGB using sRGB gamma correction
  const [r, g, b] = rgb.map(c => {
    const normalized = c / 255
    return normalized <= 0.04045 
      ? normalized / 12.92 
      : Math.pow((normalized + 0.055) / 1.055, 2.4)
  })

  // Apply ITU-R BT.709 coefficients for luminance calculation
  return 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0)
}

/**
 * Calculates the contrast ratio between two RGB colors according to WCAG 2.1
 * @param {RGB} rgb1 - First RGB color as [number, number, number]
 * @param {RGB} rgb2 - Second RGB color as [number, number, number]
 * @returns {number} Contrast ratio [1-21]
 * @example
 * // Calculate contrast between black and white
 * getContrastRatio([0, 0, 0], [255, 255, 255]) // 21
 */
export function getContrastRatio(rgb1: RGB, rgb2: RGB): number {
  const lum1 = getLuminance(rgb1)
  const lum2 = getLuminance(rgb2)

  const brightest = Math.max(lum1, lum2)
  const darkest = Math.min(lum1, lum2)

  return (brightest + 0.05) / (darkest + 0.05)
}

/**
 * Determines WCAG compliance level for a given contrast ratio
 * @param {number} contrastRatio - Contrast ratio value
 * @returns {'AAA' | 'AA' | 'FAIL'} WCAG compliance level
 * @example
 * // Check compliance levels
 * getWcagLevel(7.5) // 'AAA'
 * getWcagLevel(5.0) // 'AA'
 * getWcagLevel(3.0) // 'FAIL'
 */
export function getWcagLevel(contrastRatio: number): 'AAA' | 'AA' | 'FAIL' {
  if (contrastRatio >= 7) return 'AAA'
  if (contrastRatio >= 4.5) return 'AA'
  return 'FAIL'
}

/**
 * Determines if an RGB color is considered dark using perceptual brightness
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @param {'perceptual' | 'yiq'} [method='yiq'] - Method to use for darkness calculation
 * @returns {boolean} True if the color is dark
 * @example
 * // Check if colors are dark
 * isDark([0, 0, 0]) // true
 * isDark([255, 255, 255]) // false
 */
export function isDark(rgb: RGB, method: 'perceptual' | 'yiq' = 'yiq'): boolean {
  if (method === 'perceptual') {
    // Use perceptual brightness calculation based on human vision sensitivity
    const brightness = (rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114)
      return brightness < 128
  }

  // YIQ equation from http://24ways.org/2010/calculating-color-contrast
  const yiq = (rgb[0] * 2126 + rgb[1] * 7152 + rgb[2] * 722) / 10000
  return yiq < 128
}

/**
 * Determines if an RGB color is considered light
 * @param {RGB} rgb - RGB color values [0-255, 0-255, 0-255]
 * @returns {boolean} True if the color is light
 */
export function isLight(rgb: RGB): boolean {
  return !isDark(rgb)
}

// lighten is imported from transforms.ts to avoid duplication
// darken is imported from transforms.ts to avoid duplication
// saturate is imported from transforms.ts to avoid duplication
// desaturate is imported from transforms.ts to avoid duplication
// rotateHue is imported from transforms.ts to avoid duplication
// adjustOpacity is imported from transforms.ts to avoid duplication

// mixColors is imported from transforms.ts to avoid duplication

/**
 * Calculates the perceptual distance between two colors using Delta E CIE76
 * @param {RGB} rgb1 - First RGB color as [number, number, number]
 * @param {RGB} rgb2 - Second RGB color as [number, number, number]
 * @returns {number} Perceptual distance value (0-100+)
 */
export function getColorDistance(rgb1: RGB, rgb2: RGB): number {
  const lab1 = rgbToLab(rgb1)
  const lab2 = rgbToLab(rgb2)
  
  const deltaL = lab1[0] - lab2[0]
  const deltaA = lab1[1] - lab2[1]
  const deltaB = lab1[2] - lab2[2]
  
  return Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB)
}

/**
 * Determines if two colors are similar based on perceptual distance
 * @param {RGB} rgb1 - First RGB color as [number, number, number]
 * @param {RGB} rgb2 - Second RGB color as [number, number, number]
 * @param {number} [threshold=10] - Similarity threshold (lower = more similar required)
 * @returns {boolean} True if colors are similar
 */
export function areColorsSimilar(rgb1: RGB, rgb2: RGB, threshold = 10): boolean {
  return getColorDistance(rgb1, rgb2) < threshold
}


/**
 * Get a tonal color (same hue/sat, different lightness)
 * @param {string} baseHex - Base color hex string (e.g. "#ff0000")
 * @param {number} t - 0 (darkest) to 1 (lightest)
 * @param {[number, number]} [lightnessRange=[10, 95]] - [min, max] lightness
 * @returns {string} Hex color string
 */
export function getTonalColor(
  baseHex: string,
  t: number,
  lightnessRange: [number, number] = [10, 95]
): string {
  const rgb = hexToRgb(baseHex)
  if (!rgb) return baseHex
  const hsl = rgbToHsl(rgb)
  const l = clamp(lightnessRange[0] + (lightnessRange[1] - lightnessRange[0]) * t, 5, 95)
  return rgbToHex(hslToRgb([hsl[0], hsl[1], l]))
}

/**
 * Get a color with modified saturation
 * @param {string} baseHex - Base color hex string (e.g. "#ff0000")
 * @param {number} saturation - New saturation value (0-100)
 * @returns {string} Hex color string
 */
export function withSaturation(baseHex: string, saturation: number): string {
  const rgb = hexToRgb(baseHex)
  if (!rgb) return baseHex
  const hsl = rgbToHsl(rgb)
  return rgbToHex(hslToRgb([hsl[0], clamp(saturation, 0, 100), hsl[2]]))
}

/**
 * Get a color with modified lightness
 * @param {string} baseHex - Base color hex string (e.g. "#ff0000")
 * @param {number} lightness - New lightness value (0-100)
 * @returns {string} Hex color string
 */
export function withLightness(baseHex: string, lightness: number): string {
  const rgb = hexToRgb(baseHex)
  if (!rgb) return baseHex
  const hsl = rgbToHsl(rgb)
  return rgbToHex(hslToRgb([hsl[0], hsl[1], clamp(lightness, 0, 100)]))
}

/**
 * Get a color with modified hue
 * @param {string} baseHex - Base color hex string (e.g. "#ff0000")
 * @param {number} hue - New hue value (0-360)
 * @returns {string} Hex color string
 */
export function withHue(baseHex: string, hue: number): string {
  const rgb = hexToRgb(baseHex)
  if (!rgb) return baseHex
  const hsl = rgbToHsl(rgb)
  return rgbToHex(hslToRgb([normalizeHue(hue), hsl[1], hsl[2]]))
}

/**
 * Get a color with modified alpha (opacity)
 * (returns rgba string)
 * @param {string} baseHex - Base color hex string (e.g. "#ff0000")
 * @param {number} alpha - Alpha value (0-1)
 * @returns {string} RGBA color string (e.g. "rgba(255,0,0,0.5)")
 */
export function withAlpha(baseHex: string, alpha: number): string {
  const rgb = hexToRgb(baseHex)
  if (!rgb) return baseHex
  return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${clamp(alpha, 0, 1)})`
}

/**
 * Get the complementary color of a given hex color.
 * @param {string} baseHex - Base color hex string (e.g. "#ff0000")
 * @returns {string} Complementary color hex string
 */
export function getComplementary(baseHex: string): string {
  const rgb = hexToRgb(baseHex)
  if (!rgb) return baseHex
  const hsl = rgbToHsl(rgb)
  const compHue = normalizeHue(hsl[0] + 180)
  return rgbToHex(hslToRgb([compHue, hsl[1], hsl[2]]))
}

/**
 * Get the two triadic colors of a given hex color.
 * @param {string} baseHex - Base color hex string (e.g. "#ff0000")
 * @returns {string[]} Array of two triadic color hex strings
 */
export function getTriadic(baseHex: string): string[] {
  const rgb = hexToRgb(baseHex)
  if (!rgb) return [baseHex]
  const hsl = rgbToHsl(rgb)
  const hue1 = normalizeHue(hsl[0] + 120)
  const hue2 = normalizeHue(hsl[0] + 240)
  return [
    rgbToHex(hslToRgb([hue1, hsl[1], hsl[2]])),
    rgbToHex(hslToRgb([hue2, hsl[1], hsl[2]]))
  ]
}

/**
 * Get the three tetradic colors of a given hex color.
 * @param {string} baseHex - Base color hex string (e.g. "#ff0000")
 * @returns {string[]} Array of three tetradic color hex strings
 */
export function getTetradic(baseHex: string): string[] {
  const rgb = hexToRgb(baseHex)
  if (!rgb) return [baseHex]
  const hsl = rgbToHsl(rgb)
  const hues = [
    normalizeHue(hsl[0] + 90),
    normalizeHue(hsl[0] + 180),
    normalizeHue(hsl[0] + 270)
  ]
  return hues.map(hue => rgbToHex(hslToRgb([hue, hsl[1], hsl[2]])))
}

/**
 * Get the two split-complementary colors of a given hex color.
 * @param {string} baseHex - Base color hex string (e.g. "#ff0000")
 * @param {number} angle - Angle offset from complementary (default 30)
 * @returns {string[]} Array of two split-complementary color hex strings
 */
export function getSplitComplementary(baseHex: string, angle = 30): string[] {
  const rgb = hexToRgb(baseHex)
  if (!rgb) return [baseHex]
  const hsl = rgbToHsl(rgb)
  const comp = hsl[0] + 180
  const hue1 = normalizeHue(comp - angle)
  const hue2 = normalizeHue(comp + angle)
  return [
    rgbToHex(hslToRgb([hue1, hsl[1], hsl[2]])),
    rgbToHex(hslToRgb([hue2, hsl[1], hsl[2]]))
  ]
}

/**
 * Get the two analogous colors of a given hex color.
 * @param {string} baseHex - Base color hex string (e.g. "#ff0000")
 * @param {number} angle - Angle offset for analogous colors (default 30)
 * @returns {string[]} Array of two analogous color hex strings
 */
export function getAnalogous(baseHex: string, angle = 30): string[] {
  const rgb = hexToRgb(baseHex)
  if (!rgb) return [baseHex]
  const hsl = rgbToHsl(rgb)
  const hue1 = normalizeHue(hsl[0] - angle)
  const hue2 = normalizeHue(hsl[0] + angle)
  return [
    rgbToHex(hslToRgb([hue1, hsl[1], hsl[2]])),
    rgbToHex(hslToRgb([hue2, hsl[1], hsl[2]]))
  ]
}

/**
 * Get the two rectangle (double-complementary) colors of a given hex color.
 * @param {string} baseHex - Base color hex string (e.g. "#ff0000")
 * @returns {string[]} Array of three rectangle color hex strings
 */
export function getRectangle(baseHex: string): string[] {
  const rgb = hexToRgb(baseHex)
  if (!rgb) return [baseHex]
  const hsl = rgbToHsl(rgb)
  // Rectangle: base, base+60, base+180, base+240
  const hues = [
    normalizeHue(hsl[0] + 60),
    normalizeHue(hsl[0] + 180),
    normalizeHue(hsl[0] + 240)
  ]
  return hues.map(hue => rgbToHex(hslToRgb([hue, hsl[1], hsl[2]])))
}

/**
 * Finds the closest color from a palette to a target color
 * @param {RGB} target - Target RGB color as [number, number, number]
 * @param {RGB[]} palette - Array of RGB colors to search
 * @returns {RGB | null} Closest color from palette, or null if palette is empty
 */
export function findClosestColor(target: RGB, palette: RGB[]): RGB | null {
  if (palette.length === 0) return null
  
  const firstColor = palette[0]
  if (!firstColor) return null
  
  let closest = firstColor
  let minDistance = getColorDistance(target, closest)
  
  for (let i = 1; i < palette.length; i++) {
    const currentColor = palette[i]
    if (!currentColor) continue
    
    const distance = getColorDistance(target, currentColor)
    if (distance < minDistance) {
      minDistance = distance
      closest = currentColor
    }
  }
  
  return closest
}

/**
 * Generates a color harmony palette based on a base color and harmony type
 * @param {string} baseHex - Base color in hex format (e.g. "#ff0000")
 * @param {HarmonyType} harmonyType - Type of color harmony to generate
 * @param {number} count - Number of colors to generate
 * @returns {string[]} Array of hex color strings
 */
export function generateHarmony(baseHex: string, harmonyType: HarmonyType, count: number): string[] {
  const rgb = hexToRgb(baseHex)
  if (!rgb) return [baseHex]

  const hsl = rgbToHsl(rgb)
  const baseHue = hsl[0]
  const baseSat = hsl[1]
  const baseLight = hsl[2]

  // Harmony angle definitions (degrees)
  const harmonyAngles: Record<HarmonyType, number[]> = {
    'monochromatic': [0],
    'complementary': [0, 180],
    'split-complementary': [0, 150, 210],
    'analogous': [-40, -20, 0, 20, 40],
    'triadic': [0, 120, 240],
    'tetradic': [0, 90, 180, 270],
    'square': [0, 90, 180, 270],
    'double-complementary': [0, 60, 180, 240],
    'rectangle': [0, 60, 180, 240]
  }

  // How many unique hues for this harmony?
  let angles: number[] = []
  if (harmonyType === 'monochromatic') {
    angles = [0]
  } else if (harmonyType in harmonyAngles) {
    angles = harmonyAngles[harmonyType]
    // If count > angles.length, repeat or interpolate
    if (count > angles.length) {
      // Interpolate evenly around the circle for the given harmony
      if (
        harmonyType === 'analogous' ||
        harmonyType === 'triadic' ||
        harmonyType === 'tetradic' ||
        harmonyType === 'square' ||
        harmonyType === 'double-complementary' ||
        harmonyType === 'rectangle'
      ) {
        // Evenly distribute hues around the circle
        const step = 360 / count
        angles = Array.from({ length: count }, (_, i) => i * step)
      } else {
        // For complementary/split-complementary, repeat angles
        while (angles.length < count) {
          angles = angles.concat(harmonyAngles[harmonyType])
        }
        angles = angles.slice(0, count)
      }
    } else {
      angles = angles.slice(0, count)
    }
  } else {
    angles = [0]
  }

  // For monochromatic, generate tints/shades by varying lightness
  if (harmonyType === 'monochromatic') {
    const minL = Math.max(10, baseLight - 30)
    const maxL = Math.min(90, baseLight + 30)
    const step = (maxL - minL) / Math.max(1, count - 1)
    return Array.from({ length: count }, (_, i) => {
      const l = clamp(minL + i * step, 5, 95)
      return rgbToHex(hslToRgb([baseHue, baseSat, l]))
    })
  }

  // For harmonies, generate colors at each angle, then optionally add tints/shades for larger palettes
  const palette: string[] = []
  const satRange: [number, number] = [clamp(baseSat - 20, 30, 100), clamp(baseSat + 20, 30, 100)]
  const lightRange: [number, number] = [clamp(baseLight - 20, 15, 90), clamp(baseLight + 20, 15, 90)]

  for (let i = 0; i < count; i++) {
    // Distribute hues evenly for the harmony
    const angle = angles[i % angles.length]
    // Type-safety: angle may be undefined if angles is empty, but we always ensure at least [0]
    const hue = normalizeHue(baseHue + (typeof angle === 'number' ? angle : 0))
    // For larger palettes, vary lightness and saturation in a wave
    const sat =
      count > 4
        ? clamp(
            satRange[0] +
              ((satRange[1] - satRange[0]) *
                (0.5 - 0.5 * Math.cos((Math.PI * i) / (count - 1)))),
            20,
            100
          )
        : baseSat
    const light =
      count > 4
        ? clamp(
            lightRange[0] +
              ((lightRange[1] - lightRange[0]) *
                (0.5 - 0.5 * Math.cos((Math.PI * i) / (count - 1)))),
            10,
            95
          )
        : baseLight
    palette.push(rgbToHex(hslToRgb([hue, sat, light])))
  }

  // Remove duplicates and always include the base color as the first
  const baseHexNorm = rgbToHex(rgb)
  const uniquePalette = [baseHexNorm, ...palette.filter(
    (hex, idx) => hex !== baseHexNorm && palette.indexOf(hex) === idx
  )]
  // Limit to count
  return uniquePalette.slice(0, count)
}

/**
 * Generates a tonal palette with consistent hue and saturation but varying lightness
 * @param baseHex - Base color in hex format
 * @param count - Number of colors to generate
 * @param lightnessRange - Range of lightness variation (0-100)
 * @returns Array of hex color strings
 */
export function generateTonalPalette(baseHex: string, count: number, lightnessRange = 80): string[] {
  const rgb = hexToRgb(baseHex)
  if (!rgb) return [baseHex]

  const hsl = rgbToHsl(rgb)
  if (count <= 1) return [baseHex]
  // Center the ramp on the base color's lightness
  const minL = clamp(hsl[2] - lightnessRange / 2, 5, 95)
  const maxL = clamp(hsl[2] + lightnessRange / 2, 5, 95)
  // Use perceptual spacing (cosine ramp)
  return Array.from({ length: count }, (_, i) => {
    const t = i / (count - 1)
    const l = clamp(
      minL + (maxL - minL) * (0.5 - 0.5 * Math.cos(Math.PI * t)),
      5,
      95
    )
    return rgbToHex(hslToRgb([hsl[0], hsl[1], l]))
  })
}

/**
 * Generates a complete color scale (50-900) from a base color.
 * Useful for design systems (e.g., Tailwind-style).
 * @param baseHex - Base color in hex format
 * @param {Object} options - Options for scale generation
 * @param {string} options.scaleType - Type of scale ('linear' | 'perceptual' | 'oklch')
 * @param {boolean} options.preserveHue - Whether to preserve the base hue
 * @returns {Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900, string>} Record of scale steps to hex color
 */
export function generateColorScale(
  baseHex: string,
  options?: {
    scaleType?: 'linear' | 'perceptual' | 'oklch'
    preserveHue?: boolean
  }
): Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900, string> {
  // For now, use perceptual/cosine ramp in HSL, can be extended for oklch
  const steps = [50,100,200,300,400,500,600,700,800,900] as const
  const rgb = hexToRgb(baseHex)
  if (!rgb) {
    // fallback: gray scale
    return Object.fromEntries(steps.map(s => [s, baseHex])) as Record<typeof steps[number], string>
  }
  const hsl = rgbToHsl(rgb)
  // Lightness range: 95 (50) to 10 (900)
  const minL = 10
  const maxL = 95
  const preserveHue = options?.preserveHue ?? true
  const scale: Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900, string> = {} as Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900, string>
  steps.forEach((step, i) => {
    const t = i / (steps.length - 1)
    let l: number
    if (options?.scaleType === 'linear') {
      l = maxL + (minL - maxL) * t
    } else {
      // perceptual/cosine
      l = clamp(
        maxL + (minL - maxL) * (0.5 - 0.5 * Math.cos(Math.PI * t)),
        5,
        95
      )
    }
    // Optionally, vary saturation for more depth
    let s = hsl[1]
    if (options?.scaleType === 'perceptual' || !options?.scaleType) {
      s = clamp(hsl[1] + (t - 0.5) * 30, 20, 100)
    }
    // Optionally, preserve or slightly shift hue
    let hue = hsl[0]
    if (!preserveHue) {
      hue = normalizeHue(hsl[0] + (t - 0.5) * 10)
    }
    scale[step] = rgbToHex(hslToRgb([hue, s, l]))
  })
  return scale
}

/**
 * Generates a palette with colors that have good visual separation.
 * Modular, supports vibrancy, harmony, and palette size options.
 * @param {string} baseHex - The base color in hex format (e.g. "#ff0000").
 * @param {object} [options] - Options for palette generation.
 * @param {number} [options.count=5] - Number of colors to generate.
 * @param {number} [options.minDistance=30] - Minimum perceptual distance between colors.
 * @param {number} [options.saturation] - Override base saturation (0-100).
 * @param {number} [options.lightness] - Override base lightness (0-100).
 * @param {'muted'|'balanced'|'vibrant'|'intense'} [options.vibrancy] - Vibrancy preset.
 * @param {'even'|'random'} [options.hueStrategy='even'] - How to distribute hues.
 * @returns {string[]} Array of hex color strings.
 */
export function generateDistinctPalette(
  baseHex: string,
  options?: {
    count?: number
    minDistance?: number
    saturation?: number
    lightness?: number
    vibrancy?: 'muted' | 'balanced' | 'vibrant' | 'intense'
    hueStrategy?: 'even' | 'random'
  }
): string[] {
  const count = options?.count ?? 5
  const minDistance = options?.minDistance ?? 30
  const hueStrategy = options?.hueStrategy ?? 'even'
  const rgb = hexToRgb(baseHex)
  if (!rgb) return [baseHex]
  const hsl = rgbToHsl(rgb)
  let baseSat = options?.saturation ?? clamp(hsl[1], 40, 90)
  const baseLight = options?.lightness ?? clamp(hsl[2], 30, 70)

  // Vibrancy presets
  if (options?.vibrancy) {
    switch (options.vibrancy) {
      case 'muted':
        baseSat = Math.max(0, baseSat * 0.4)
        break
      case 'balanced':
        baseSat = Math.max(0, baseSat * 0.7)
        break
      case 'vibrant':
        baseSat = Math.min(100, baseSat * 1.2)
        break
      case 'intense':
        baseSat = Math.min(100, baseSat * 1.4)
        break
    }
  }

  const baseHue = hsl[0]
  const palette: string[] = []
  const usedHues: number[] = []

  for (let i = 0; i < count; i++) {
    let hue: number
    if (hueStrategy === 'even') {
      hue = normalizeHue(baseHue + i * (360 / count))
    } else {
      // random, but avoid close to usedHues
      let tries = 0
      do {
        hue = normalizeHue(baseHue + Math.random() * 360)
        tries++
      } while (
        usedHues.some(h => Math.abs(h - hue) < 20) &&
        tries < 10
      )
    }
    usedHues.push(hue)

    // Vary lightness and saturation slightly for depth
    const sat = clamp(
      baseSat + 10 * Math.sin((2 * Math.PI * i) / count),
      35,
      100
    )
    const light = clamp(
      baseLight + 10 * Math.cos((2 * Math.PI * i) / count),
      15,
      90
    )
    const color = rgbToHex(hslToRgb([hue, sat, light]))
    // Ensure minimum perceptual distance from all previous
    if (
      palette.every(existing => {
        const colorRgb = hexToRgb(color)
        const existingRgb = hexToRgb(existing)
        if (!colorRgb || !existingRgb) return false
        return getColorDistance(colorRgb, existingRgb) >= minDistance
      })
    ) {
      palette.push(color)
    } else {
      // Try a random hue if too close
      let tries = 0
      let found = false
      while (tries < 10 && !found) {
        const randomHue = normalizeHue(baseHue + Math.random() * 360)
        const randomColor = rgbToHex(
          hslToRgb([
            randomHue,
            clamp(baseSat + (Math.random() - 0.5) * 30, 35, 100),
            clamp(baseLight + (Math.random() - 0.5) * 40, 15, 90)
          ])
        )
        const randomColorRgb = hexToRgb(randomColor)
        if (
          randomColorRgb &&
          palette.every(existing => {
            const existingRgb = hexToRgb(existing)
            if (!existingRgb) return false
            return getColorDistance(randomColorRgb, existingRgb) >= minDistance
          })
        ) {
          palette.push(randomColor)
          found = true
        }
        tries++
      }
      if (!found) palette.push(color)
    }
  }
  // Always include the base color as the first
  const baseHexNorm = rgbToHex(rgb)
  if (!palette.includes(baseHexNorm)) palette.unshift(baseHexNorm)
  return palette.slice(0, count)
}

/**
 * Checks if a color combination meets WCAG accessibility standards
 * @param {string} foreground - Foreground color (hex string)
 * @param {string} background - Background color (hex string)
 * @param {WcagLevel} [level='aa'] - WCAG level to check
 * @returns {boolean} True if the combination meets the accessibility standard
 */
export function meetsAccessibility(
  foreground: string,
  background: string,
  level: WcagLevel = 'aa'
): boolean {
  const fgRgb = hexToRgb(foreground)
  const bgRgb = hexToRgb(background)
  
  if (!fgRgb || !bgRgb) return false
  
  const ratio = getContrastRatio(fgRgb, bgRgb)
  return level === 'aa' ? ratio >= 4.5 : ratio >= 7
}

/**
 * Finds the best contrasting color from a palette for accessibility
 * @param {string} baseColor - Base color to contrast against (hex string)
 * @param {string[]} palette - Array of colors to choose from (hex strings)
 * @param {WcagLevel} [level='aa'] - Minimum WCAG level required
 * @returns {string | null} Best contrasting color or null if none meet requirements
 */
export function findBestContrast(
  baseColor: string,
  palette: string[],
  level: WcagLevel = 'aa'
): string | null {
  const baseRgb = hexToRgb(baseColor)
  if (!baseRgb) return null
  
  let bestColor: string | null = null
  let bestRatio = 0
  const minRatio = level === 'aa' ? 4.5 : 7

  for (const color of palette) {
    const colorRgb = hexToRgb(color)
    if (!colorRgb) continue
    
    const ratio = getContrastRatio(baseRgb, colorRgb)
    
    if (ratio >= minRatio && ratio > bestRatio) {
      bestRatio = ratio
      bestColor = color
    }
  }

  return bestColor
}

/**
 * Generates a palette of colors with guaranteed accessibility contrast
 * @param {string} baseColor - Base color (hex string)
 * @param {number} count - Number of colors to generate
 * @param {WcagLevel} [level='aa'] - WCAG level to maintain
 * @returns {string[]} Array of accessible colors
 */
export function generateAccessiblePalette(
  baseColor: string,
  count: number,
  level: WcagLevel = 'aa'
): string[] {
  const baseRgb = hexToRgb(baseColor)
  if (!baseRgb || count < 1) return [baseColor]
  // Generate a tonal ramp (dark to light)
  const tonal = generateTonalPalette(baseColor, Math.max(count * 2, 8))
  // Filter for accessible contrast
  const accessible = tonal.filter(
    (hex) =>
      hex !== baseColor &&
      meetsAccessibility(hex, baseColor, level) &&
      meetsAccessibility(baseColor, hex, level)
  )
  // Always include the base color as the first
  const result = [baseColor, ...accessible.slice(0, count - 1)]
  // If not enough, fill with best-contrast colors from the ramp
  if (result.length < count) {
    const extras = tonal.filter(
      (hex) => !result.includes(hex)
    )
    for (const hex of extras) {
      if (result.length >= count) break
      result.push(hex)
    }
  }
  return result.slice(0, count)
}

/**
 * Generates a complete semantic palette for web/UI design.
 * Modular, supports custom harmony and shade options.
 * @param {string} primaryColor - Main brand color (hex string)
 * @param {object} [options] - Options for palette generation
 * @param {HarmonyType} [options.secondaryHarmony] - Harmony type for secondary color
 * @param {boolean} [options.includeShades=true] - Whether to include shades/tints for each semantic color
 * @param {number} [options.shadesCount=7] - Number of shades/tints per color
 * @returns {SemanticPalette} SemanticPalette object
 */
export function generateSemanticPalette(
  primaryColor: string,
  options?: {
    secondaryHarmony?: HarmonyType
    includeShades?: boolean
    shadesCount?: number
  }
): SemanticPalette {
  const shadesCount = options?.shadesCount ?? 7
  const includeShades = options?.includeShades ?? true
  // Primary: tonal ramp
  const primary = includeShades
    ? generateTonalPalette(primaryColor, shadesCount)
    : [primaryColor]
  // Secondary: harmony from primary
  const secondaryHarmony: HarmonyType = options?.secondaryHarmony ?? 'complementary'
  const secondaryCandidates = generateHarmony(primaryColor, secondaryHarmony, 2)
  const secondaryColor = secondaryCandidates.find((c): c is string => typeof c === 'string' && c !== primaryColor) ?? (typeof secondaryCandidates[0] === 'string' ? secondaryCandidates[0] : primaryColor)
  const secondary = includeShades
    ? generateTonalPalette(secondaryColor, shadesCount)
    : [secondaryColor]
  // Accent: triadic or analogous
  const accentCandidates = generateHarmony(primaryColor, 'triadic', 3)
  const accent = accentCandidates.filter((c): c is string => !!c && c !== primaryColor && c !== secondaryColor)
  // Neutral: gray scale
  const neutralScale = generateColorScale('#888888', { scaleType: 'perceptual' })
  const neutral = neutralScale ? Object.values(neutralScale) : ['#888888']
  // Semantic: success, warning, error, info
  const semantic: SemanticPalette['semantic'] = {
    success: generateTonalPalette('#22c55e', shadesCount), // green
    warning: generateTonalPalette('#eab308', shadesCount), // yellow
    error: generateTonalPalette('#ef4444', shadesCount),   // red
    info: generateTonalPalette('#3b82f6', shadesCount)     // blue
  }
  return {
    primary,
    secondary,
    accent,
    neutral,
    semantic
  }
}

/**
 * Analyzes a palette for quality, variety, and accessibility.
 * @param {string[]} colors - Array of hex color strings
 * @returns {PaletteAnalysis} PaletteAnalysis object - see @link {PaletteAnalysis}
 * @example
 * {
 *  variety: {
 *    hueSpread: 360,
 *    saturationRange: 100,
 *    lightnessRange: 100
 *  },
 *  balance: {
 *    warmCoolRatio: 0.5,
 *    lightDarkRatio: 0.5
 *    vibrancyScore: 0.5
 *  },
 *  accessibility: {
 *    validPairs: 10
 *    problematicPairs: [[0, 1], [0, 2], [0, 3], [0, 4], [1, 2], [1, 3], [1, 4], [2, 3], [2, 4], [3, 4]]
 *  },
 *  issues: ['Palette has low hue variety', 'Palette has low saturation variety', 'Palette has low lightness variety', 'Some color pairs have insufficient contrast']
 *  score: 100
 * }
 */
export function analyzePalette(colors: string[]): PaletteAnalysis {
  // Variety: hue spread, saturation range, lightness range
  const hsls = colors.map(hex => {
    const rgb = hexToRgb(hex)
    return rgb ? rgbToHsl(rgb) : [0, 0, 0]
  })
  const hues = hsls.map(hsl => hsl[0])
  const sats = hsls.map(hsl => hsl[1])
  const lights = hsls.map(hsl => hsl[2])
  const hueSpread = hues.length > 1 ? Math.max(...hues) - Math.min(...hues) : 0
  const saturationRange = sats.length > 1 ? Math.max(...sats) - Math.min(...sats) : 0
  const lightnessRange = lights.length > 1 ? Math.max(...lights) - Math.min(...lights) : 0
  // Balance: warm/cool, light/dark, vibrancy
  let warm = 0, cool = 0, light = 0, dark = 0, vibrancy = 0
  for (const hex of colors) {
    const rgb = hexToRgb(hex)
    if (!rgb) continue
    const temp = getColorTemperature(rgb)
    if (temp === 'warm') warm++
    else if (temp === 'cool') cool++
    const l = rgbToHsl(rgb)[2]
    if (l > 60) light++
    else dark++
    const s = rgbToHsl(rgb)[1]
    if (s > 60) vibrancy++
  }
  const warmCoolRatio = (cool + warm) > 0 ? warm / (cool + warm) : 0.5
  const lightDarkRatio = (light + dark) > 0 ? light / (light + dark) : 0.5
  const vibrancyScore = colors.length > 0 ? vibrancy / colors.length : 0
  // Accessibility: count valid pairs
  let validPairs = 0
  const problematicPairs: Array<[number, number]> = []
  for (let i = 0; i < colors.length; i++) {
    for (let j = i + 1; j < colors.length; j++) {
      const color1 = colors[i]
      const color2 = colors[j]
      if (!color1 || !color2) continue
      const rgb1 = hexToRgb(color1)
      const rgb2 = hexToRgb(color2)
      if (!rgb1 || !rgb2) continue
      const ratio = getContrastRatio(rgb1, rgb2)
      if (ratio >= 4.5) validPairs++
      else problematicPairs.push([i, j])
    }
  }
  // Issues
  const issues: string[] = []
  if (hueSpread < 30) issues.push('Palette has low hue variety')
  if (saturationRange < 20) issues.push('Palette has low saturation variety')
  if (lightnessRange < 20) issues.push('Palette has low lightness variety')
  if (problematicPairs.length > 0) issues.push('Some color pairs have insufficient contrast')
  // Score: simple weighted sum
  let score = 100
  score -= issues.length * 10
  score -= problematicPairs.length * 2
  score -= (30 - hueSpread) * 0.2
  score = Math.max(0, Math.min(100, Math.round(score)))
  return {
    variety: {
      hueSpread,
      saturationRange,
      lightnessRange
    },
    balance: {
      warmCoolRatio,
      lightDarkRatio,
      vibrancyScore
    },
    accessibility: {
      validPairs,
      problematicPairs
    },
    issues,
    score
  }
}

/**
 * Suggests improvements for a palette.
 * Modular, returns improved palette and list of changes.
 * @param {string[]} colors - Array of hex color strings
 * @param {'web'|'brand'|'data-viz'|'print'} targetUse - Target use case
 * @returns {{ improved: string[], changes: string[] }} Object with improved palette and list of changes
 */
export function improvePalette(
  colors: string[],
  _targetUse: 'web' | 'brand' | 'data-viz' | 'print'
): { improved: string[]; changes: string[] } {
  const analysis = analyzePalette(colors)
  const improved = [...colors]
  const changes: string[] = []

  // Defensive: get first color safely
  const baseColor: string | undefined = colors.length > 0 ? colors[0] : undefined

  // Improve hue variety
  if (analysis.variety.hueSpread < 30 && baseColor) {
    // Add a complementary or triadic color
    const harmony = generateHarmony(baseColor, 'complementary', 2)
    const newColor = harmony.length > 1 ? harmony[1] : undefined
    if (typeof newColor === 'string') {
      improved.push(newColor)
      changes.push('Added complementary color for hue variety')
    }
  }
  // Improve saturation variety
  if (analysis.variety.saturationRange < 20 && baseColor) {
    // Add a more saturated and a more muted version
    const rgb = hexToRgb(baseColor)
    if (rgb) {
      const hsl = rgbToHsl(rgb)
      improved.push(rgbToHex(hslToRgb([hsl[0], 90, hsl[2]])))
      improved.push(rgbToHex(hslToRgb([hsl[0], 10, hsl[2]])))
      changes.push('Added saturated and muted variants')
    }
  }
  // Improve lightness variety
  if (analysis.variety.lightnessRange < 20 && baseColor) {
    // Add a lighter and a darker version
    const rgb = hexToRgb(baseColor)
    if (rgb) {
      const hsl = rgbToHsl(rgb)
      improved.push(rgbToHex(hslToRgb([hsl[0], hsl[1], 90])))
      improved.push(rgbToHex(hslToRgb([hsl[0], hsl[1], 15])))
      changes.push('Added lighter and darker variants')
    }
  }
  // Improve accessibility
  if (analysis.accessibility.problematicPairs.length > 0) {
    // For each problematic pair, try to adjust lightness for contrast
    for (const [i, j] of analysis.accessibility.problematicPairs) {
      const color1 = improved[i]
      const color2 = improved[j]
      if (!color1 || !color2) continue
      const rgb1 = hexToRgb(color1)
      const rgb2 = hexToRgb(color2)
      if (!rgb1 || !rgb2) continue
      const hsl1 = rgbToHsl(rgb1)
      const hsl2 = rgbToHsl(rgb2)
      const l1 = hsl1[2]
      const l2 = hsl2[2]
      if (Math.abs(l1 - l2) < 40) {
        // Make one lighter, one darker
        improved[i] = rgbToHex(hslToRgb([hsl1[0], hsl1[1], clamp(l1 + 30, 10, 95)]))
        improved[j] = rgbToHex(hslToRgb([hsl2[0], hsl2[1], clamp(l2 - 30, 10, 95)]))
        changes.push(`Adjusted lightness for contrast between color ${i} and ${j}`)
      }
    }
  }
  return { improved: Array.from(new Set(improved)), changes }
}

/**
 * Generates a palette that balances multiple harmony types.
 * Modular, supports contrast and temperature options.
 * @param {string} baseColor - Base color (hex string)
 * @param {object} options - Options for palette generation
 * @param {number} options.size - Number of colors in the palette
 * @param {HarmonyType[]} options.includeHarmonies - Harmony types to include
 * @param {boolean} [options.ensureContrast] - Whether to ensure minimum contrast between colors
 * @param {'mixed'|'warm'|'cool'} [options.temperatureBalance] - Desired temperature balance
 * @returns {string[]} Array of hex color strings
 */
export function generateBalancedPalette(
  baseColor: string,
  options: {
    size: number
    includeHarmonies: HarmonyType[]
    ensureContrast?: boolean
    temperatureBalance?: 'mixed' | 'warm' | 'cool'
  }
): string[] {
  const harmonies = options.includeHarmonies
  let palette: string[] = []
  // Gather colors from all harmonies
  for (const harmony of harmonies) {
    palette = palette.concat(
      generateHarmony(baseColor, harmony, Math.ceil(options.size / harmonies.length))
        .filter((c): c is string => typeof c === 'string')
    )
  }
  // Remove duplicates and limit to size
  palette = Array.from(new Set(palette)).filter((c): c is string => !!c).slice(0, options.size)
  // Optionally, filter for temperature
  if (options.temperatureBalance && options.temperatureBalance !== 'mixed') {
    palette = palette.filter(hex => {
      const rgb = hexToRgb(hex)
      if (!rgb) return false
      const temp = getColorTemperature(rgb)
      return temp === options.temperatureBalance
    })
    // If not enough, fill with base harmony
    if (palette.length < options.size) {
      // Only call generateHarmony if harmonies[0] is defined
      const fallbackHarmony: HarmonyType | undefined = harmonies[0]
      if (fallbackHarmony !== undefined) {
        palette = palette.concat(
          generateHarmony(baseColor, fallbackHarmony, options.size - palette.length)
            .filter((c): c is string => typeof c === 'string')
        )
        palette = Array.from(new Set(palette)).slice(0, options.size)
      }
    }
  }
  // Optionally, ensure contrast
  if (options.ensureContrast) {
    const filtered: string[] = []
    for (const hex of palette) {
      if (filtered.every(existing => {
        const rgb1 = hexToRgb(hex)
        const rgb2 = hexToRgb(existing)
        if (!rgb1 || !rgb2) return true
        return getContrastRatio(rgb1, rgb2) >= 2.5
      })) {
        filtered.push(hex)
      }
      if (filtered.length >= options.size) break
    }
    palette = filtered
  }
  return palette.slice(0, options.size)
}

/**
 * Generates perfect text/background pairs from a palette.
 * @param {string[]} palette - Array of hex color strings
 * @param {number} [minContrast=4.5] - Minimum contrast ratio
 * @returns {Array<{ background: string, text: string, ratio: number }>} Array of objects with background, text, and ratio
 */
export function generateContrastPairs(
  palette: string[],
  minContrast = 4.5
): Array<{ background: string; text: string; ratio: number }> {
  const pairs: Array<{ background: string; text: string; ratio: number }> = []
  for (const bg of palette) {
    let best: { text: string; ratio: number } = { text: bg, ratio: 0 }
    for (const text of palette) {
      if (text === bg) continue
      const rgbBg = hexToRgb(bg)
      const rgbText = hexToRgb(text)
      if (!rgbBg || !rgbText) continue
      const ratio = getContrastRatio(rgbBg, rgbText)
      if (ratio > best.ratio && ratio >= minContrast) {
        best = { text, ratio }
      }
    }
    if (best.ratio >= minContrast) {
      pairs.push({ background: bg, text: best.text, ratio: best.ratio })
    }
  }
  return pairs
}

/**
 * Ensures a palette has good distribution of properties.
 * Modular, supports constraints for lightness, saturation, temperature, and accessibility.
 * @param {string[]} colors - Array of hex color strings
 * @param {object} constraints - Constraints for balancing
 * @param {number} [constraints.minLightnessSpread] - Minimum lightness spread
 * @param {number} [constraints.minSaturationSpread] - Minimum saturation spread
 * @param {boolean} [constraints.requireWarmAndCool] - Require both warm and cool colors
 * @param {boolean} [constraints.ensureAccessiblePairs] - Ensure accessible color pairs
 * @returns {string[]} Balanced palette as array of hex color strings
 */
export function balancePalette(
  colors: string[],
  constraints: {
    minLightnessSpread?: number
    minSaturationSpread?: number
    requireWarmAndCool?: boolean
    ensureAccessiblePairs?: boolean
  }
): string[] {
  let palette = [...colors]
  const hsls = palette.map(hex => {
    const rgb = hexToRgb(hex)
    return rgb ? rgbToHsl(rgb) : [0, 0, 0]
  })
  // Lightness spread
  if (constraints.minLightnessSpread) {
    const lights = hsls.map(hsl => hsl[2])
    const spread = Math.max(...lights) - Math.min(...lights)
    if (spread < constraints.minLightnessSpread) {
      // Add lighter and darker variants
      const base = palette[0]
      if (typeof base === 'string') {
        const rgb = hexToRgb(base)
        if (rgb) {
          const hsl = rgbToHsl(rgb)
          palette.push(rgbToHex(hslToRgb([hsl[0], hsl[1], 95])))
          palette.push(rgbToHex(hslToRgb([hsl[0], hsl[1], 10])))
        }
      }
    }
  }
  // Saturation spread
  if (constraints.minSaturationSpread) {
    const sats = hsls.map(hsl => hsl[1])
    const spread = Math.max(...sats) - Math.min(...sats)
    if (spread < constraints.minSaturationSpread) {
      const base = palette[0]
      if (typeof base === 'string') {
        const rgb = hexToRgb(base)
        if (rgb) {
          const hsl = rgbToHsl(rgb)
          palette.push(rgbToHex(hslToRgb([hsl[0], 90, hsl[2]])))
          palette.push(rgbToHex(hslToRgb([hsl[0], 10, hsl[2]])))
        }
      }
    }
  }
  // Require warm and cool
  if (constraints.requireWarmAndCool) {
    let hasWarm = false, hasCool = false
    for (const hex of palette) {
      const rgb = hexToRgb(hex)
      if (!rgb) continue
      const temp = getColorTemperature(rgb)
      if (temp === 'warm') hasWarm = true
      if (temp === 'cool') hasCool = true
    }
    if (!hasWarm) {
      const base = palette[0]
      if (typeof base === 'string') {
        const warm = generateHarmony(base, 'analogous', 3).find(hex => {
          const rgb = hexToRgb(hex)
          return !!rgb && getColorTemperature(rgb) === 'warm'
        })
        if (typeof warm === 'string') palette.push(warm)
      }
    }
    if (!hasCool) {
      const base = palette[0]
      if (typeof base === 'string') {
        const cool = generateHarmony(base, 'analogous', 3).find(hex => {
          const rgb = hexToRgb(hex)
          return !!rgb && getColorTemperature(rgb) === 'cool'
        })
        if (typeof cool === 'string') palette.push(cool)
      }
    }
  }
  // Ensure accessible pairs
  if (constraints.ensureAccessiblePairs) {
    const filtered: string[] = []
    for (const hex of palette) {
      if (filtered.every(existing => {
        const rgb1 = hexToRgb(hex)
        const rgb2 = hexToRgb(existing)
        if (!rgb1 || !rgb2) return true
        return getContrastRatio(rgb1, rgb2) >= 3
      })) {
        filtered.push(hex)
      }
    }
    palette = filtered
  }
  return Array.from(new Set(palette))
}

/**
 * Fills gaps in a palette to improve variety.
 * Modular, supports expansion by harmony, contrast, tonal, or balanced strategies.
 * @param {string[]} existingColors - Array of hex color strings
 * @param {number} targetSize - Desired palette size
 * @param {'harmony'|'contrast'|'tonal'|'balanced'} strategy - Strategy for expansion
 * @returns {string[]} Expanded palette as array of hex color strings
 */
export function expandPalette(
  existingColors: string[],
  targetSize: number,
  strategy: 'harmony' | 'contrast' | 'tonal' | 'balanced'
): string[] {
  let palette = [...existingColors]
  while (palette.length < targetSize) {
    if (strategy === 'harmony') {
      const base = palette[0]
      if (typeof base === 'string') {
        const next = generateHarmony(base, 'triadic', targetSize).filter((c): c is string => !!c)
        palette = Array.from(new Set([...palette, ...next]))
      }
    } else if (strategy === 'contrast') {
      // Add color with max contrast to all existing
      let best: string | null = null
      let bestMinRatio = 0
      for (let h = 0; h < 360; h += 10) {
        const candidate = rgbToHex(hslToRgb([h, 70, 50]))
        const minRatio = Math.min(...palette.map(hex => {
          const rgb1 = hexToRgb(candidate)
          const rgb2 = hexToRgb(hex)
          if (!rgb1 || !rgb2) return 0
          return getContrastRatio(rgb1, rgb2)
        }))
        if (minRatio > bestMinRatio) {
          bestMinRatio = minRatio
          best = candidate
        }
      }
      if (best) palette.push(best)
    } else if (strategy === 'tonal') {
      // Add lighter/darker variants
      const base = palette[0]
      if (typeof base === 'string') {
        const rgb = hexToRgb(base)
        if (rgb) {
          const hsl = rgbToHsl(rgb)
          palette.push(rgbToHex(hslToRgb([hsl[0], hsl[1], clamp(hsl[2] + 20, 10, 95)])))
          palette.push(rgbToHex(hslToRgb([hsl[0], hsl[1], clamp(hsl[2] - 20, 10, 95)])))
        }
      }
    } else if (strategy === 'balanced') {
      // Mix harmony and contrast
      const base = palette[0]
      if (typeof base === 'string') {
        const next = generateHarmony(base, 'tetradic', targetSize).filter((c): c is string => !!c)
        palette = Array.from(new Set([...palette, ...next]))
      }
    }
    palette = Array.from(new Set(palette))
    if (palette.length > targetSize) palette = palette.slice(0, targetSize)
  }
  return palette.slice(0, targetSize)
}

/**
 * Analyzes the emotional associations of a color.
 * @param {RGB} rgb - RGB color values
 * @returns {{ primary: string[], secondary: string[], energy: 'high'|'medium'|'low' }} Object with emotional associations
 */
export function getColorEmotions(rgb: RGB): {
  primary: string[]
  secondary: string[]
  energy: 'high' | 'medium' | 'low'
} {
  const hsl = rgbToHsl(rgb)
  const [hue, saturation, lightness] = hsl
  
  const primary: string[] = []
  const secondary: string[] = []
  
  // Hue-based emotions (color psychology)
  if (hue >= 0 && hue < 30) primary.push('passionate', 'energetic')
  else if (hue >= 30 && hue < 60) primary.push('cheerful', 'optimistic')
  else if (hue >= 60 && hue < 120) primary.push('natural', 'peaceful')
  else if (hue >= 120 && hue < 180) primary.push('calming', 'trustworthy')
  else if (hue >= 180 && hue < 240) primary.push('cool', 'professional')
  else if (hue >= 240 && hue < 300) primary.push('creative', 'mysterious')
  else primary.push('luxurious', 'romantic')
  
  // Saturation-based emotions
  if (saturation > 70) secondary.push('vibrant', 'bold')
  else if (saturation > 30) secondary.push('balanced', 'pleasant')
  else secondary.push('subtle', 'sophisticated')
  
  // Lightness-based emotions
  if (lightness > 70) secondary.push('light', 'airy')
  else if (lightness < 30) secondary.push('serious', 'dramatic')
  
  // Energy level
  const energy = saturation > 60 && lightness > 40 ? 'high' : 
                saturation > 30 || lightness > 60 ? 'medium' : 'low'
  
  return { primary, secondary, energy }
}

/**
 * Suggests the best use cases for a color based on its properties.
 * @param {RGB} rgb - RGB color values
 * @returns {string[]} Array of suggested use cases
 */
export function getColorUseCases(rgb: RGB): string[] {
  const useCases: string[] = []
  const hsl = rgbToHsl(rgb)
  const [hue, saturation, _] = hsl
  const temperature = getColorTemperature(rgb)
  const brightness = getBrightnessLevel(rgb)
  
  // Based on temperature
  if (temperature === 'warm') {
    useCases.push('call-to-action buttons', 'accent colors', 'food & dining')
  } else if (temperature === 'cool') {
    useCases.push('backgrounds', 'technology', 'healthcare')
  }
  
  // Based on brightness
  if (brightness === 'very-light') {
    useCases.push('backgrounds', 'subtle highlights')
  } else if (brightness === 'very-dark') {
    useCases.push('text', 'borders', 'shadows')
  }
  
  // Based on saturation
  if (saturation > 70) {
    useCases.push('brand colors', 'highlights', 'warnings')
  } else if (saturation < 20) {
    useCases.push('neutral backgrounds', 'text', 'borders')
  }
  
  // Hue-specific use cases
  if (hue >= 0 && hue < 60) useCases.push('energy', 'food', 'warnings')
  else if (hue >= 60 && hue < 180) useCases.push('nature', 'success', 'growth')
  else if (hue >= 180 && hue < 270) useCases.push('trust', 'technology', 'calm')
  else useCases.push('luxury', 'creativity', 'spirituality')
  
  return [...new Set(useCases)] // Remove duplicates
}
